import { hexToWorld } from '../hex.ts';
import { dist2, idiv, imax, imin, mulBp, withinRange } from '../math/fixed.ts';
import { rngInt, rngRange } from '../math/rng.ts';
import type { BattleState, Enemy, ErmState, Hero, HermitRules, LitterRules, LitterState, SendaNpc } from '../types.ts';
import { damageEnemy, damageHero, ev, spawnEnemy } from './combat.ts';
import { stepToward } from './enemies.ts';
import { orderMove } from './heroes.ts';
import { naveTrace } from './ermita-layout.ts';
import { sendaCue } from './senda.ts';
import { velFresh, velGoal, velHold, velHoly, velMarkAt, velNoHeal, velOrder, velResult, velTaken, velTick } from './vigilia.ts';

/**
 * The cleric's last road, chapter 4 (docs/sendas/clerigo.md, «La Ermita»; the nave in battle/ermita-layout.ts, numbers in
 * content CAMINO.hermit, vigil, nave). The stretcher's own rule (battle/litter.ts) carries Tessa and her limpets; this
 * adds what each act does on top, through the hooks litter.ts calls (`ermTick`, `ermHold`, `ermOrder`, `ermMarkAt`,
 * `ermBusy`, `ermResult`, `ermGoal`, `ermHoly`), and two more combat.ts asks: `ermShield` (the Doliente in the memory: no
 * blow reaches her, but the holy light she takes in a wail counts) and `ermNoHeal` (her wail: no heal works).
 *
 * - The memory, Aud's last vigil (`vigil`): the hero is Madre Aud. Six lamps from the nave's door to the altar, each one
 *   free to light a minute after the one before. The Doliente comes in by the atrium and comes up the nave: a lit lamp
 *   ahead of her stops her, and she reaches for it with a tongue of ink that crawls along the floor; a tongue held at a
 *   lamp puts it out, and she comes on to it. A tongue is cut with any blow. A lamp lit again behind her drives her back
 *   to it. Every so often she weeps (warned first): while she weeps no heal works; holy light on her in it cuts it and she
 *   keeps quiet a while (a wail not cut makes the next tongue quicker). Whoever stands by her veil takes it, through
 *   armour. The dead come in from the atrium; a bone singer among them. Lost if the altar's lamp goes out, or is out at
 *   dawn; won at dawn.
 * - The nave of the mirrors (`nave`, the test): Tessa and Piet at the altar. Aud's mirror out of its place: taken from the
 *   loose stone, set in its place. Six mirrors (three positions) and two dividers (two): the lantern's beam to the altar
 *   and the door at once (a beam through two dividers lights nothing), held a few seconds. Limpets out of the ink on the
 *   walls: some for a mirror on the beam's way (one under a limpet stops it, until a blow peels it: then it leaps at the
 *   hero), the rest for Tessa. The great bone singer and the dead from the cells.
 *
 * Cost: six lamps or eight pieces and a handful of nightmares looked at once a tick; the beam worked out again only when
 * a piece turns, Aud's mirror is set or a limpet covers or leaves a mirror.
 */

/** The marks: a lamp `ERM_LAMP + i`, a mirror `ERM_MIRROR + i`, a divider `ERM_SPLIT + i`, the loose stone, Aud's mirror's place. */
export const ERM_LAMP = 20;
export const ERM_MIRROR = 40;
export const ERM_SPLIT = 50;
export const ERM_STONE = 60;
export const ERM_SLOT = 61;
const NEVER = 0x7fffffff;

const person = (s: BattleState, id: string): SendaNpc | undefined => s.senda?.npcs.find((n) => n.id === id);
const scaled = (s: BattleState, e: Enemy): Enemy => {
  if (s.rules?.enemyHpBp) e.hp = e.maxHp = imax(1, mulBp(e.maxHp, s.rules.enemyHpBp));
  return e;
};

const fresh = (H: HermitRules): ErmState => {
  const V = H.vigil;
  const N = H.nave;
  return {
    lit: V ? V.lamps.map(() => 0) : [],
    front: -1,
    boss: 0,
    tongue: V ? V.in + V.tongue.every : NEVER,
    tongues: [],
    wail: V ? V.wail.first : NEVER,
    wailUntil: 0,
    mute: 0,
    holy: 0,
    veil: 0,
    fast: 0,
    dead: V ? V.dead.first : N ? N.dead.first : NEVER,
    singer: 0,
    splits: N ? [...N.split0] : [],
    aud: N?.set ? 2 : 0,
    covered: 0,
    both: -1,
    altar: false,
    door: false,
    segs: [],
    weak: [],
    onMirror: [],
    next: N ? N.limpet.first : NEVER,
    dirge: N ? N.dirge.first : NEVER,
    ...(H.vela ? { vel: velFresh(H.vela) } : {}),
  };
};

const ermOf = (s: BattleState): { r: LitterRules; H: HermitRules; w: LitterState; m: ErmState } | null => {
  const r = s.rules?.senda?.litter;
  const w = s.senda?.litter;
  const H = r?.hermit;
  const m = w?.erm;
  return r && H && w && m ? { r, H, w, m } : null;
};

/** A lamp of the memory free to light (its minute come). */
export const ermLampReady = (H: HermitRules, i: number, waveT: number): boolean => !!H.vigil && waveT >= H.vigil.every * i;

/** The Doliente is weeping: no heal works (the memory). */
export const ermNoHeal = (s: BattleState): boolean => {
  const o = ermOf(s);
  if (o?.H.vela) return velNoHeal(s);
  return !!o && !!o.H.vigil && s.waveT < o.m.wailUntil && o.m.boss > 0;
};

/** What a blow on a nightmare comes to (chapter 5: the Doliente quiet takes more; holy light in her wail counts). */
export const ermTaken = (s: BattleState, e: Enemy, amount: number, type: string | undefined): number => velTaken(s, e, amount, type);

/** The Doliente in the memory: no blow reaches her; holy light on her while she weeps counts toward hushing her. */
export const ermShield = (s: BattleState, e: Enemy, amount: number, type: string | undefined): boolean => {
  const o = ermOf(s);
  if (!o || !o.H.vigil || e.id !== o.m.boss) return false;
  if (type === 'holy' && ermNoHeal(s)) o.m.holy += amount;
  return true;
};

/* ---------- the hooks litter.ts calls ---------- */

const lampAt = (H: HermitRules, i: number): { x: number; y: number } | null => H.vigil?.lamps[i] ?? null;

/** The point of a mark (a lamp, a mirror, a divider, the loose stone, Aud's mirror's place). */
export const ermMarkAt = (s: BattleState, at: number): { x: number; y: number } | null => {
  const H = s.rules?.senda?.litter?.hermit;
  if (!H) return null;
  if (H.vela) {
    const p = velMarkAt(s, at);
    if (p) return p;
  }
  if (at >= ERM_LAMP && at < ERM_LAMP + 6) return lampAt(H, at - ERM_LAMP);
  if (at === ERM_STONE) return H.nave ? { ...H.nave.stone } : null;
  const mi = at === ERM_SLOT ? (H.nave?.aud ?? -1) : at - ERM_MIRROR;
  if (at === ERM_SLOT || (at >= ERM_MIRROR && at < ERM_MIRROR + 6)) {
    const m = H.mirrors[mi];
    return m ? hexToWorld(m.q, m.r) : null;
  }
  if (at >= ERM_SPLIT && at < ERM_SPLIT + 2) {
    const p = H.splits[at - ERM_SPLIT];
    return p ? hexToWorld(p.q, p.r) : null;
  }
  return null;
};

/** Sends the hero to a lamp, a mirror, a divider, the loose stone or Aud's mirror's place. */
export const ermOrder = (s: BattleState, h: Hero, at: number): void => {
  const o = ermOf(s);
  if (!o) return;
  const { H, w, m } = o;
  if (H.vela && velOrder(s, h, at)) return;
  let p: { x: number; y: number } | null = null;
  if (at >= ERM_LAMP && at < ERM_LAMP + 6) {
    const l = lampAt(H, at - ERM_LAMP);
    // (Beside it, toward the nave's axis.)
    if (l) p = { x: l.x + (l.x > 0 ? -450 : 450), y: l.y + 200 };
  } else if (at === ERM_STONE && H.nave) p = { ...H.nave.stone, y: H.nave.stone.y + 600 };
  else if (at === ERM_SLOT && H.nave) p = H.mirrors[H.nave.aud]?.stand ?? null;
  else if (at >= ERM_MIRROR && at < ERM_MIRROR + 6) p = H.mirrors[at - ERM_MIRROR]?.stand ?? null;
  else if (at >= ERM_SPLIT && at < ERM_SPLIT + 2) p = H.splits[at - ERM_SPLIT]?.stand ?? null;
  if (!p || (H.nave && m.both >= 0 && s.tick - m.both >= H.nave.hold)) return;
  w.job = at;
  w.held = 0;
  orderMove(s, h, p.x, p.y);
  h.target = 0;
  h.forced = false;
};

/** Nightmares at their own work: the Doliente and her tongues, limpets on a mirror. */
export const ermBusy = (s: BattleState, e: Enemy): boolean => {
  // (Chapter 5's people and nightmares go by its own rule: battle/vigilia.ts.)
  if (s.rules?.senda?.litter?.hermit?.vela) return true;
  const m = s.senda?.litter?.erm;
  return !!m && (e.id === m.boss || m.tongues.some((t) => t.id === e.id) || m.onMirror.some((x) => x.id === e.id));
};

/** A holy blow on a limpet on a mirror peels it: it leaps at the one who struck (the stretcher's own rule from there). */
export const ermHoly = (s: BattleState, e: Enemy): boolean => {
  const o = ermOf(s);
  if (!o) return false;
  if (o.H.vela && velHoly(s, e)) return true;
  const k = o.m.onMirror.findIndex((x) => x.id === e.id);
  if (k < 0) return false;
  o.m.onMirror.splice(k, 1);
  o.w.limpets.push({ id: e.id, on: 3, t: s.tick });
  ev(s, { t: 'senda', kind: 'peel', npc: '', x: e.x, y: e.y });
  return true;
};

/* ---------- the work by a mark ---------- */

const work = (s: BattleState, o: NonNullable<ReturnType<typeof ermOf>>, h: Hero | undefined, cue: (c: string) => void): void => {
  const { r, H, w, m } = o;
  if (w.job < 0 || !h) return;
  const at = w.job;
  const p = ermMarkAt(s, at);
  if (!p) {
    w.job = -1;
    return;
  }
  const piece = at >= ERM_MIRROR && at !== ERM_STONE;
  if (h.moving || !withinRange(h.x, h.y, p.x, p.y, H.reach + (piece ? 300 : 0))) {
    // Standing still out of reach (no way there, or told elsewhere): let it go after a while, never stuck waiting.
    if (h.moving || w.held > 0) w.held = 0;
    else if (--w.held < -24) {
      w.held = 0;
      w.job = -1;
    }
    return;
  }
  if (w.held < 0) w.held = 0;
  let need = H.stand;
  const V = H.vigil;
  const N = H.nave;
  if (at >= ERM_LAMP && at < ERM_LAMP + 6) {
    const i = at - ERM_LAMP;
    if (!V || m.lit[i] || !ermLampReady(H, i, s.waveT)) {
      w.job = -1;
      return;
    }
    need = V.light;
  } else if (at === ERM_STONE || at === ERM_SLOT) {
    if (!N || (at === ERM_STONE ? m.aud !== 0 : m.aud !== 1)) {
      w.job = -1;
      return;
    }
    need = N.take;
  } else if (N && at === ERM_MIRROR + N.aud && m.aud !== 2) {
    // (Its place is empty: nothing to turn yet.)
    w.job = -1;
    return;
  }
  if (++w.held < need) return;
  w.held = 0;
  w.job = -1;
  if (at >= ERM_LAMP && at < ERM_LAMP + 6) {
    const i = at - ERM_LAMP;
    m.lit[i] = 1;
    ev(s, { t: 'senda', kind: 'kindle', npc: '', x: p.x, y: p.y, n: i });
    // Lit again behind her: the light drives her back to it.
    if (m.front >= i) {
      m.front = i - 1;
      ev(s, { t: 'senda', kind: 'driven', npc: '', x: p.x, y: p.y, n: i });
    }
    cue(`lamp${i + 1}`);
    return;
  }
  if (at === ERM_STONE) {
    m.aud = 1;
    ev(s, { t: 'senda', kind: 'take', npc: '', x: p.x, y: p.y, n: -1 });
    return;
  }
  if (at === ERM_SLOT) {
    m.aud = 2;
    ev(s, { t: 'senda', kind: 'set', npc: '', x: p.x, y: p.y, n: N?.aud ?? 0 });
    cue('set');
    rebeam(s, r, H, w, m, cue);
    return;
  }
  if (at >= ERM_SPLIT) {
    const i = at - ERM_SPLIT;
    m.splits[i] = 1 - (m.splits[i] ?? 0);
    ev(s, { t: 'senda', kind: 'mirror', npc: '', x: p.x, y: p.y, n: 6 + i });
  } else {
    const i = at - ERM_MIRROR;
    w.turns[i] = ((w.turns[i] ?? 0) + 1) % 3;
    ev(s, { t: 'senda', kind: 'mirror', npc: '', x: p.x, y: p.y, n: i });
  }
  rebeam(s, r, H, w, m, cue);
};

/* ---------- the nave of the mirrors ---------- */

/** A mirror frosted over (chapter 5) doesn't turn the beam: it goes on straight, as if it weren't there. */
const frosted = (m: ErmState): number => ((m.vel?.frost ?? -1) >= 0 ? 1 << (m.vel?.frost as number) : 0);

/** The beam worked out again: its pieces for the drawing (world points), how faint each is, the altar and the door. */
const rebeam = (s: BattleState, r: LitterRules, H: HermitRules, w: LitterState, m: ErmState, cue: (c: string) => void): void => {
  const N = H.nave;
  if (!N) return;
  const B = { lantern: H.lantern, altar: H.altar, door: H.door, mirrors: H.mirrors, splits: H.splits };
  const b = naveTrace(B, w.turns, m.splits, (m.aud === 2 ? 0 : 1 << N.aud) | frosted(m), m.covered);
  m.segs = [];
  for (let i = 0; i + 3 < b.segs.length; i += 4) {
    const a = hexToWorld(b.segs[i] as number, b.segs[i + 1] as number);
    const c = hexToWorld(b.segs[i + 2] as number, b.segs[i + 3] as number);
    m.segs.push(a.x, a.y, c.x, c.y);
  }
  m.weak = [...b.weak];
  w.beam = [];
  w.hits = 0;
  for (let i = 0; i < 6; i++) if ((b.touched >> i) & 1) w.hits++;
  const was = m.altar && m.door;
  m.altar = b.altar;
  m.door = b.door;
  if (b.altar && b.door) {
    if (!was) {
      m.both = s.tick;
      const c = hexToWorld(H.altar.q, H.altar.r);
      ev(s, { t: 'senda', kind: 'lamp', npc: '', x: c.x, y: c.y });
    }
  } else m.both = -1;
  if (b.altar && !b.door) cue('altarOnly');
  if (b.weak.some((k) => k >= 2)) cue('weak');
  void r;
};

/** The mirrors on the beam's way now (bits), Aud's only once it is in its place. */
const onWay = (H: HermitRules, w: LitterState, m: ErmState): number => {
  const N = H.nave;
  if (!N) return 0;
  const b = naveTrace({ lantern: H.lantern, altar: H.altar, door: H.door, mirrors: H.mirrors, splits: H.splits }, w.turns, m.splits, (m.aud === 2 ? 0 : 1 << N.aud) | frosted(m), m.covered);
  return b.touched & 0x3f;
};

const naveTick = (s: BattleState, o: NonNullable<ReturnType<typeof ermOf>>, h: Hero | undefined, cue: (c: string) => void): void => {
  const { r, H, w, m } = o;
  const N = H.nave;
  if (!N) return;
  if (w.turns.length !== H.mirrors.length) {
    w.turns = [...N.start];
    rebeam(s, r, H, w, m, cue);
  }
  if (m.both >= 0 && s.tick - m.both >= N.hold) return;
  // The great bone singer, from the start, by the door (none in chapter 5's).
  if (!m.singer && N.singer > 0) {
    const e = spawnEnemy(s, 'bonesinger', 2, false, 0, H.atrium.x, H.atrium.y - 1400, N.singer);
    m.singer = e.id;
  }
  // Its dirge: a rain of bones on the hero wherever it is, while it stands.
  const sg = s.enemies.find((e) => e.id === m.singer && e.hp > 0);
  // (Chapter 5 has no singer: the wind's hail down the hole in the roof on the hero, wherever, on the same clock.)
  const hail = N.singer === 0;
  if (sg && s.waveT === m.dirge - N.dirge.warn) ev(s, { t: 'senda', kind: 'dirgeWarn', npc: '', x: sg.x, y: sg.y, n: N.dirge.warn });
  if (hail && h && s.waveT === m.dirge - N.dirge.warn) ev(s, { t: 'senda', kind: 'hailWarn', npc: '', x: h.x, y: h.y, n: N.dirge.warn });
  if (s.waveT >= m.dirge) {
    m.dirge = s.waveT + N.dirge.every;
    if (sg && h) {
      sg.anim = 'attack';
      sg.animT = 0;
      damageHero(s, h, mulBp(h.stats.maxHp, N.dirge.bp), sg, 'shadow', true);
      ev(s, { t: 'senda', kind: 'dirge', npc: '', x: h.x, y: h.y });
      cue('dirge');
    } else if (hail && h && N.dirge.bp > 0) {
      damageHero(s, h, mulBp(h.stats.maxHp, N.dirge.bp), null, 'frost', true);
      ev(s, { t: 'senda', kind: 'hail', npc: '', x: h.x, y: h.y });
      cue('hail');
    }
  }
  // The dead from the cells.
  if (s.waveT >= m.dead) {
    m.dead = s.waveT + N.dead.every;
    const n = rngRange(s.rng, N.dead.n[0], N.dead.n[1]) + (N.dead.grow > 0 ? idiv(s.waveT, N.dead.grow) : 0);
    for (let k = 0; k < n; k++) {
      const c = H.cells[(k + idiv(s.waveT, N.dead.every)) % H.cells.length] as { x: number; y: number };
      scaled(s, spawnEnemy(s, 'skeleton', 1, false, 0, c.x, c.y + k * 300));
    }
  }
  // The limpets out of the ink: some for a mirror on the beam's way, the rest for Tessa.
  const L = N.limpet;
  if (m.onMirror.length) m.onMirror = m.onMirror.filter((x) => s.enemies.some((e) => e.id === x.id && e.hp > 0));
  if (s.waveT >= m.next) {
    m.next = s.waveT + L.trickle;
    if (w.limpets.length + m.onMirror.length < L.cap) {
      const p = H.ink[rngInt(s.rng, H.ink.length)] as { x: number; y: number };
      const e = spawnEnemy(s, 'dragger', 1, false, 0, p.x, p.y, L.hp);
      e.speed = L.speed;
      e.anim = 'idle';
      ev(s, { t: 'senda', kind: 'limpetOut', npc: '', x: p.x, y: p.y });
      const way = onWay(H, w, m) & ~m.covered;
      const free: number[] = [];
      for (let i = 0; i < 6; i++) if ((way >> i) & 1 && !m.onMirror.some((x) => x.m === i)) free.push(i);
      if (free.length && rngInt(s.rng, 10000) < L.mirrorBp) {
        // (The nearest of them to where it came out.)
        let best = free[0] as number;
        let bd = NEVER;
        for (const i of free) {
          const c = H.mirrors[i] as { q: number; r: number };
          const q = hexToWorld(c.q, c.r);
          const d = dist2(q.x, q.y, p.x, p.y);
          if (d < bd) {
            bd = d;
            best = i;
          }
        }
        m.onMirror.push({ id: e.id, m: best });
      } else w.limpets.push({ id: e.id, on: 0, t: s.tick });
    }
  }
  // Which mirrors are covered: a limpet stuck on one (within reach of it).
  let cov = 0;
  for (const x of m.onMirror) {
    const e = s.enemies.find((q) => q.id === x.id);
    const c = H.mirrors[x.m] as { q: number; r: number };
    const p = hexToWorld(c.q, c.r);
    if (e && withinRange(e.x, e.y, p.x, p.y, 460)) cov |= 1 << x.m;
  }
  if (cov !== m.covered) {
    const more = cov & ~m.covered;
    m.covered = cov;
    if (more) cue('covered');
    rebeam(s, r, H, w, m, cue);
  }
  if (h && s.tick % 12 === 0) {
    const a = hexToWorld(H.altar.q, H.altar.r);
    if (withinRange(h.x, h.y, a.x, a.y, 4200)) cue('inscription');
  }
  // The beam burns the ink it crosses (not a limpet holding a mirror's face: it sits behind the glass's edge).
  if (s.tick % 12 === 0 && m.segs.length >= 4)
    for (const e of s.enemies) {
      if (e.hp <= 0 || e.id === m.singer || m.onMirror.some((x) => x.id === e.id)) continue;
      for (let i = 0; i + 3 < m.segs.length; i += 4) {
        if ((m.weak[i / 4] ?? 0) >= 2) continue;
        if (segD2(e.x, e.y, m.segs[i] as number, m.segs[i + 1] as number, m.segs[i + 2] as number, m.segs[i + 3] as number) <= 450 * 450) {
          damageEnemy(s, e, N.burn, { pid: '', type: 'holy' });
          break;
        }
      }
    }
  if (m.both >= 0 && s.tick - m.both === N.hold) {
    cue('lit');
    // (The nave won keeps for chapter 5: its seed.)
    if (s.senda) s.senda.nums.nave = H.naveSeed ?? s.seed;
  }
};

const segD2 = (px: number, py: number, ax: number, ay: number, bx: number, by: number): number => {
  const dx = bx - ax;
  const dy = by - ay;
  const len2 = dx * dx + dy * dy;
  let t = len2 > 0 ? idiv(((px - ax) * dx + (py - ay) * dy) * 1000, len2) : 0;
  t = t < 0 ? 0 : t > 1000 ? 1000 : t;
  return dist2(px, py, ax + idiv(dx * t, 1000), ay + idiv(dy * t, 1000));
};

/* ---------- the memory: Aud's last vigil ---------- */

/** The first lit lamp ahead of the Doliente, or −1. */
const ahead = (m: ErmState): number => {
  for (let i = m.front + 1; i < m.lit.length; i++) if (m.lit[i]) return i;
  return -1;
};

/** Where she stands: short of the lit lamp ahead of her, or on to the next one (never past the fifth while the altar's is dark). */
const dolGoal = (H: HermitRules, m: ErmState): { x: number; y: number } => {
  const V = H.vigil as NonNullable<HermitRules['vigil']>;
  const j = ahead(m);
  if (j >= 0) {
    const l = V.lamps[j] as { x: number; y: number };
    const back = m.front >= 0 ? (V.lamps[m.front] as { x: number; y: number }) : H.atrium;
    // A quarter of the way from where she is held to the lamp she reaches for: her tongues do the rest.
    return { x: back.x + idiv((l.x - back.x) * 25, 100), y: back.y + idiv((l.y - back.y) * 25, 100) };
  }
  const k = imin(m.front + 1, V.lamps.length - 2);
  return k >= 0 ? { ...(V.lamps[k] as { x: number; y: number }) } : { ...H.atrium };
};

const vigilTick = (s: BattleState, o: NonNullable<ReturnType<typeof ermOf>>, h: Hero | undefined, cue: (c: string) => void): void => {
  const { H, w, m } = o;
  const V = H.vigil;
  if (!V) return;
  // Tessa's mother on the altar, Tessa at twenty-two kneeling by her.
  const a = hexToWorld(H.altar.q, H.altar.r);
  const mo = person(s, 'tessaMother');
  if (mo) Object.assign(mo, { x: a.x, y: a.y + 150, moving: false, facing: 768 });
  const ty = person(s, 'tessaYoung');
  if (ty) Object.assign(ty, { x: a.x + (a.x > 0 ? -800 : 800), y: a.y + 650, moving: false, facing: a.x > 0 ? 0 : 512 });
  void w;
  if (s.waveT >= V.dawn) return;
  // She comes in by the atrium.
  if (!m.boss && s.waveT >= V.in) {
    const e = spawnEnemy(s, 'wraith', 3, false, 0, H.atrium.x, H.atrium.y, 1_000_000);
    m.boss = e.id;
    ev(s, { t: 'senda', kind: 'bossIn', npc: '', x: e.x, y: e.y });
    cue('in');
  }
  const b = m.boss ? s.enemies.find((e) => e.id === m.boss && e.hp > 0) : undefined;
  if (b) {
    b.stun = 0;
    // Her wail: warned, then in; cut by holy light in it; one not cut makes the next tongue quicker.
    if (m.wailUntil > 0 && s.waveT === m.wailUntil && m.holy < V.wail.hush) m.fast++;
    if (s.waveT === m.wail - V.wail.warn) ev(s, { t: 'senda', kind: 'wailWarn', npc: '', x: b.x, y: b.y, n: V.wail.warn });
    if (s.waveT === m.wail) {
      m.wailUntil = m.wail + V.wail.long;
      m.holy = 0;
      m.wail += V.wail.every;
      ev(s, { t: 'senda', kind: 'wail', npc: '', x: b.x, y: b.y, n: V.wail.long });
      cue('wail1');
    }
    if (s.waveT < m.wailUntil && m.holy >= V.wail.hush) {
      m.wailUntil = s.waveT;
      m.holy = 0;
      m.mute = s.waveT + V.wail.mute;
      ev(s, { t: 'senda', kind: 'hush', npc: '', x: b.x, y: b.y });
      cue('hush');
    }
    const quiet = s.waveT < m.mute;
    // A tongue at the lamp ahead of her.
    const j = ahead(m);
    if (j >= 0 && s.waveT >= m.tongue && !quiet) {
      m.tongue = s.waveT + V.tongue.every;
      const e = spawnEnemy(s, 'dragger', 1, false, 0, b.x, b.y + 200, V.tongue.hp);
      e.speed = V.tongue.speed;
      m.tongues.push({ id: e.id, lamp: j, at: -1 });
      ev(s, { t: 'senda', kind: 'tongue', npc: '', x: b.x, y: b.y, n: j });
    }
    // Her veil, on whoever stands by her.
    if (h && !quiet && s.waveT >= m.veil && withinRange(h.x, h.y, b.x, b.y, V.veil.r)) {
      m.veil = s.waveT + V.veil.every;
      b.anim = 'attack';
      b.animT = 0;
      damageHero(s, h, mulBp(h.stats.maxHp, V.veil.bp), b, 'shadow', true);
      ev(s, { t: 'senda', kind: 'veil', npc: '', x: h.x, y: h.y });
    }
  }
  // The tongues: held at their lamp long enough, it goes out and she comes on to it.
  if (m.tongues.length) m.tongues = m.tongues.filter((t) => s.enemies.some((e) => e.id === t.id && e.hp > 0));
  for (const t of m.tongues) {
    if (t.at < 0 || !m.lit[t.lamp]) continue;
    if (s.tick - t.at < (m.fast > 0 ? V.tongue.fast : V.tongue.snuff)) continue;
    m.lit[t.lamp] = 0;
    if (m.fast > 0) m.fast--;
    m.front = imax(m.front, t.lamp);
    const l = V.lamps[t.lamp] as { x: number; y: number };
    ev(s, { t: 'senda', kind: 'snuffed', npc: '', x: l.x, y: l.y, n: t.lamp });
    cue('snuffed');
    // The altar's: the vigil is lost.
    if (t.lamp === V.lamps.length - 1) m.both = -2;
    const e = s.enemies.find((q) => q.id === t.id);
    if (e) e.hp = 0;
  }
  // A tongue whose lamp went out another way (or that she passed) lets go.
  for (const t of m.tongues) {
    if (m.lit[t.lamp] || t.at < 0) continue;
    const e = s.enemies.find((q) => q.id === t.id);
    if (e) e.hp = 0;
  }
  // The dead from the atrium, and the bone singer among them.
  if (m.boss && s.waveT >= m.dead) {
    m.dead = s.waveT + V.dead.every;
    const n = rngRange(s.rng, V.dead.n[0], V.dead.n[1]);
    for (let k = 0; k < n; k++) scaled(s, spawnEnemy(s, k === 0 && idiv(s.waveT, V.dead.every) % 2 === 1 ? 'mourner' : 'skeleton', 1, false, 0, H.atrium.x + (k - 1) * 500, H.atrium.y - 300));
  }
  if (!m.singer && s.waveT >= V.singer) {
    const e = scaled(s, spawnEnemy(s, 'bonesinger', 1, false, 0, H.atrium.x, H.atrium.y - 400));
    m.singer = e.id;
  }
};

/* ---------- the hooks ---------- */

export const ermTick = (s: BattleState, r: LitterRules, w: LitterState, h: Hero | undefined, cue: (c: string) => void): void => {
  if (!r.hermit) return;
  w.erm ??= fresh(r.hermit);
  const o = ermOf(s);
  if (!o) return;
  // Chapter 5 (battle/vigilia.ts): its lamps, its atrium, the Doliente; the nave's mirrors still turn by chapter 4's work.
  if (r.hermit.vela) {
    if (r.hermit.nave && w.turns.length !== r.hermit.mirrors.length) {
      w.turns = [...r.hermit.nave.start];
      rebeam(s, r, r.hermit, w, o.m, cue);
    }
    velTick(s, h, cue, () => work(s, o, h, cue), () => rebeam(s, r, o.H, w, o.m, cue));
    return;
  }
  work(s, o, h, cue);
  if (r.hermit.vigil) vigilTick(s, o, h, cue);
  else naveTick(s, o, h, cue);
};

/** Its own nightmares (true: handled here): the Doliente and her tongues, the limpets going for a mirror. */
export const ermHold = (s: BattleState, e: Enemy, px: (e: Enemy) => number): boolean => {
  const o = ermOf(s);
  if (!o) return false;
  const { H, m } = o;
  if (H.vela && !m.onMirror.some((x) => x.id === e.id)) return velHold(s, e, px);
  if (e.id === m.boss && H.vigil) {
    e.stun = 0;
    const g = dolGoal(H, m);
    if (s.waveT < m.mute) e.anim = 'idle';
    else if (stepToward(e, g.x, g.y, H.vigil.glide)) e.anim = s.waveT < m.wailUntil ? 'attack' : 'idle';
    else e.anim = 'walk';
    // Reaching an unlit lamp's place, she has come on to it.
    const k = m.front + 1;
    const l = H.vigil.lamps[k];
    if (l && !m.lit[k] && k < H.vigil.lamps.length - 1 && withinRange(e.x, e.y, l.x, l.y, 500)) m.front = k;
    return true;
  }
  const t = m.tongues.find((x) => x.id === e.id);
  if (t && H.vigil) {
    e.stun = 0;
    const l = H.vigil.lamps[t.lamp] as { x: number; y: number };
    if (t.at >= 0) {
      e.anim = 'attack';
      return true;
    }
    e.anim = 'walk';
    if (stepToward(e, l.x, l.y + 250, H.vigil.tongue.speed) || withinRange(e.x, e.y, l.x, l.y, 420)) {
      t.at = s.tick;
      ev(s, { t: 'senda', kind: 'stick', npc: '', x: l.x, y: l.y, n: 4 });
      sendaCue(s, 'tongue1');
    }
    return true;
  }
  // The great bone singer keeps by the nave's door, raising what falls near it; it strikes only who comes to it.
  if (H.nave && e.id === m.singer) {
    const h = s.heroes.find((x) => x.downTicks === 0);
    const home = { x: H.atrium.x, y: H.atrium.y - 1400 };
    if (h && withinRange(h.x, h.y, e.x, e.y, 1500)) return false;
    if (withinRange(e.x, e.y, home.x, home.y, 300)) e.anim = 'idle';
    else {
      e.anim = 'walk';
      stepToward(e, home.x, home.y, px(e));
    }
    return true;
  }
  const lm = m.onMirror.find((x) => x.id === e.id);
  if (lm) {
    const c = H.mirrors[lm.m] as { q: number; r: number };
    const p = hexToWorld(c.q, c.r);
    if (withinRange(e.x, e.y, p.x, p.y, 460)) {
      // On the glass, round its rim.
      const k = (e.id * 167) % 1024;
      e.x = p.x + idiv((k - 512) * 320, 1024);
      e.y = p.y + 120;
      e.anim = 'attack';
    } else {
      e.anim = 'walk';
      if (stepToward(e, p.x, p.y, px(e))) sendaCue(s, 'limpet');
    }
    return true;
  }
  return false;
};

/** How far along: the night gone by (the memory); the altar and the door lit (the nave). */
export const ermGoal = (s: BattleState): { done: number; need: number } | null => {
  const o = ermOf(s);
  const H = s.rules?.senda?.litter?.hermit;
  if (!H) return null;
  if (H.vela) return velGoal(s);
  if (H.vigil) return { done: imin(H.vigil.dawn, s.waveT), need: H.vigil.dawn };
  if (!o) return { done: 0, need: 2 };
  return { done: (o.m.altar ? 1 : 0) + (o.m.door ? 1 : 0), need: 2 };
};

/** Won or lost by its own rule: the dawn with the altar's lamp lit (lost if it goes out); both lit long enough. */
export const ermResult = (s: BattleState): 'won' | 'lost' | null => {
  const o = ermOf(s);
  if (!o) return null;
  const { H, m } = o;
  if (H.vela) return velResult(s);
  const V = H.vigil;
  if (V) {
    if (m.both === -2) return 'lost';
    if (s.waveT >= V.dawn) {
      if (!m.lit[V.lamps.length - 1]) return 'lost';
      if (s.waveT === V.dawn) {
        const l = V.lamps[V.lamps.length - 1] as { x: number; y: number };
        ev(s, { t: 'senda', kind: 'dawn', npc: '', x: l.x, y: l.y });
        sendaCue(s, 'dawn');
      }
      return 'won';
    }
    return null;
  }
  const N = H.nave;
  if (N && m.both >= 0 && s.tick - m.both >= N.hold) return 'won';
  return null;
};
